import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal, TrendingUp, Store } from "lucide-react";

const BEFORE_IMG =
  "https://images.pexels.com/photos/13369323/pexels-photo-13369323.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AFTER_IMG =
  "https://images.unsplash.com/photo-1720135885007-454165745e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwaG9uZSUyMG1vY2t1cCUyMGFwcCUyMGRhcmt8ZW58MHx8fHwxNzc3MTA2NTA5fDA&ixlib=rb-4.1.0&q=85";

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  const updateFromX = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0.01, next)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.target.setPointerCapture?.(e.pointerId);
    updateFromX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromX(e.clientX);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    e.target.releasePointerCapture?.(e.pointerId);
  };

  return (
    <section
      id="transformation"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="before-after-section"
    >
      {/* Bg accents */}
      <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#FF7A00]/15 blur-[140px]" />
      <div className="absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-[#00C853]/15 blur-[140px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5 space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00C853]" />
              The transformation
            </div>
            <h2
              className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]"
              data-testid="before-after-heading"
            >
              From{" "}
              <span className="text-gradient-saffron">offline shop</span>
              <br />
              to{" "}
              <span className="text-gradient-green">online success.</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-md">
              Drag the handle to see the before/after — a 35-year-old kirana
              becoming a digital-first brand in a single weekend.
            </p>

            <div className="flex flex-col gap-4 pt-2">
              <div className="flex items-center gap-3 dark:glass-dark glass-light rounded-2xl px-4 py-3">
                <div className="h-10 w-10 rounded-xl bg-[#FF9933]/15 grid place-items-center">
                  <Store className="h-5 w-5 text-[#FF9933]" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Before
                  </div>
                  <div className="font-semibold text-sm">
                    Walk-in only · 200 daily reach
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 dark:glass-dark glass-light rounded-2xl px-4 py-3">
                <div className="h-10 w-10 rounded-xl bg-[#00C853]/15 grid place-items-center">
                  <TrendingUp className="h-5 w-5 text-[#00C853]" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    After 48 hours
                  </div>
                  <div className="font-semibold text-sm">
                    Web · App · AI bot · 50,000+ reach
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Slider */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7"
          >
            <div
              ref={containerRef}
              className="relative w-full aspect-[4/3] sm:aspect-[16/11] rounded-3xl overflow-hidden select-none border border-border shadow-2xl"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              data-testid="before-after-slider"
            >
              {/* AFTER (full) */}
              <img
                src={AFTER_IMG}
                alt="After - digital storefront"
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#00C853]/15 via-transparent to-transparent pointer-events-none" />

              {/* BEFORE (clipped) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${pos}%` }}
              >
                <img
                  src={BEFORE_IMG}
                  alt="Before - offline shop"
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ width: `${(100 / pos) * 100}%`, maxWidth: "none" }}
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Labels */}
              <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-white text-[11px] tracking-[0.2em] uppercase font-semibold">
                Before
              </div>
              <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-[#00C853]/90 backdrop-blur text-white text-[11px] tracking-[0.2em] uppercase font-semibold">
                After · 48h
              </div>

              {/* Divider line + handle */}
              <div
                className="absolute top-0 bottom-0 w-[2px] bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.6)] pointer-events-none"
                style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white text-black grid place-items-center cursor-ew-resize shadow-[0_8px_30px_rgba(0,0,0,0.4)] hover:scale-110 transition-transform"
                style={{ left: `${pos}%` }}
                data-testid="before-after-handle"
              >
                <MoveHorizontal className="h-5 w-5" />
              </div>
            </div>

            {/* Range fallback (accessible) */}
            <input
              type="range"
              min={0}
              max={100}
              value={pos}
              onChange={(e) => setPos(Math.max(0.01, Number(e.target.value)))}
              className="mt-4 w-full accent-[#FF7A00]"
              aria-label="Before/After slider"
              data-testid="before-after-range"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
